// Client-side parsing + aggregation for the Failure Analysis view.
//
// Consumes the CSV produced by {eventServiceUrl}/dashboard/ExportFailedDocuments, which is
// already recovery-aware (the backend excludes any DocumentId that later succeeded, extending
// the succeeded lookup window to UtcNow). We therefore treat every parsed row as a
// "normalized failure" and only classify + aggregate it here.

import { classifyRootCause, RootCause, ROOT_CAUSE_ORDER } from './rootCauseClassifier';

// Column order emitted by SearchDocumentDisplay.GetHeader() in the backend.
export interface FailedDocumentRow {
    documentId: string;
    invoiceNumber: string;
    countryCode: string; // already a friendly country name from the backend
    supplier: string;
    scenario: string;
    status: string;
    errorCode: string;
    errorMessage: string;
    externalReceiptId: string;
    supplierErrorCode: string;
    startEventTime: string;
    endEventTime: string;
    mode: string;
    companyName: string;
    rootCause: RootCause;
    errorSource: ErrorSource;
}

/**
 * Provenance of a failure (tri-state, Provider vs Unknown).
 *
 * PRIMARY (authoritative) signal: the backend now exports `SupplierErrorCode`, populated by the
 * ACS projection from the document's latest SupplierResponse event. A non-empty value means the
 * provider/partner returned an error code, so the failure is attributed to the "Provider".
 *
 * FALLBACK (heuristic): rows indexed before the SupplierErrorCode field shipped (i.e. not yet
 * reprocessed) carry an empty SupplierErrorCode. For those we fall back to ExternalReceiptId: a
 * receipt/ack id means the document reached the provider, so we still label it "Provider".
 *
 * Everything else is "Unknown" — we cannot positively attribute the failure to the provider. Note
 * we deliberately do NOT claim "Service": absence of a provider signal is not proof the service
 * caused the failure (a provider may reject synchronously without a receipt, or the signal may not
 * have been captured), so "Unknown" is the honest label.
 */
export type ErrorSource = 'Provider' | 'Unknown';

export const ERROR_SOURCE_ORDER: ErrorSource[] = ['Provider', 'Unknown'];

export function classifyErrorSource(supplierErrorCode: string, externalReceiptId: string): ErrorSource {
    if (supplierErrorCode && supplierErrorCode.trim().length > 0) {
        return 'Provider'; // authoritative: the provider returned an error code
    }
    if (externalReceiptId && externalReceiptId.trim().length > 0) {
        return 'Provider'; // heuristic fallback for rows not yet reindexed with SupplierErrorCode
    }
    return 'Unknown';
}

export type TimeBin = 'Day' | 'Week' | 'Month';

export interface CountItem {
    key: string;
    count: number;
}

export interface HeatmapCell {
    country: string;
    rootCause: RootCause;
    count: number;
}

export interface SourceSplit {
    key: RootCause;
    provider: number;
    unknown: number;
    total: number;
}

export interface FailureAnalysis {
    rows: FailedDocumentRow[];
    total: number;
    byRootCause: CountItem[];
    byCountry: CountItem[];
    bySupplier: CountItem[];
    byBin: CountItem[];
    bySource: CountItem[];
    rootCauseBySource: SourceSplit[];
    countries: string[];
    rootCauses: RootCause[];
    heatmap: HeatmapCell[];
}

// Default 0-based indexes into the ExportFailedDocuments CSV, matching the column order emitted by
// SearchDocumentDisplay.GetHeader(). These are only a fallback: parseFailedDocumentsCsv resolves
// the actual indexes from the header row by name (resolveColumns), so appended/reordered columns
// don't silently shift the fields we read.
const COL = {
    documentId: 0,
    invoiceNumber: 1,
    startEventTime: 4,
    endEventTime: 5,
    countryCode: 7,
    supplier: 9,
    scenario: 10,
    status: 11,
    externalReceiptId: 12,
    errorCode: 13,
    errorMessage: 14,
    mode: 19,
    companyName: 25,
    // SupplierErrorCode is the trailing column, but its absolute index depends on backend version
    // (a later `BatchId` column was inserted before it). Rather than hard-code a version-specific
    // index, default to -1 (absent): when a legacy export omits the SupplierErrorCode header,
    // resolveColumns leaves this at -1 so the cell reads as '' instead of accidentally picking up
    // an adjacent populated column (e.g. BatchId) and misclassifying the row as provider-returned.
    supplierErrorCode: -1,
};

type ColumnMap = typeof COL;

// Header label emitted by SearchDocumentDisplay.GetHeader() for each field we read.
const COL_HEADER: Record<keyof ColumnMap, string> = {
    documentId: 'DocumentId',
    invoiceNumber: 'InvoiceNumber',
    startEventTime: 'StartEventTime',
    endEventTime: 'EndEventTime',
    countryCode: 'CountryCode',
    supplier: 'Supplier',
    scenario: 'Scenario',
    status: 'Status',
    externalReceiptId: 'ExternalReceiptId',
    errorCode: 'ErrorCode',
    errorMessage: 'ErrorMessage',
    mode: 'Mode',
    companyName: 'CompanyName',
    supplierErrorCode: 'SupplierErrorCode',
};

/**
 * Resolve each field's column index from the CSV header row (case-insensitive, matched by name).
 * Falls back to the default index when a header name is absent (e.g. an older export missing
 * SupplierErrorCode), so the parser tolerates additive backend changes without a lockstep deploy.
 */
export function resolveColumns(headerLine: string): ColumnMap {
    const names = headerLine.split(',').map((c) => c.trim().toLowerCase());
    const resolved = { ...COL };
    (Object.keys(COL_HEADER) as (keyof ColumnMap)[]).forEach((key) => {
        const idx = names.indexOf(COL_HEADER[key].toLowerCase());
        if (idx >= 0) {
            resolved[key] = idx;
        }
    });
    return resolved;
}

/**
 * Split a single CSV data line into fields. The backend (SearchDocumentDisplay.ToString) emits
 * each row as valid RFC-4180: every field is wrapped in double quotes, adjacent fields joined by
 * `","`, and any double-quote inside a field is doubled (`""`). Newlines are stripped upstream, so
 * a row never spans multiple lines. We parse with a small quote-aware state machine rather than a
 * naive `split('","')` so that free-text fields (ErrorMessage/WarningMessage) containing the
 * delimiter substring `","` — e.g. embedded provider JSON `{"code":"E12","detail":"x"}` — do not
 * shift the columns after them (which would corrupt the trailing authoritative SupplierErrorCode).
 */
export function splitCsvLine(line: string): string[] {
    const cells: string[] = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (inQuotes) {
            if (ch === '"') {
                if (line[i + 1] === '"') {
                    cur += '"';
                    i++; // consume the escaped quote pair
                } else {
                    inQuotes = false;
                }
            } else {
                cur += ch;
            }
        } else if (ch === '"') {
            inQuotes = true;
        } else if (ch === ',') {
            cells.push(cur);
            cur = '';
        } else {
            // Stray char outside quotes (shouldn't occur in well-formed rows); keep it.
            cur += ch;
        }
    }
    cells.push(cur);
    return cells;
}

/**
 * Parse the raw ExportFailedDocuments CSV text into typed rows, classifying each row's
 * ErrorMessage into a root cause. Rows whose column count doesn't match the header are skipped
 * defensively (e.g. an unescaped quote/newline inside ErrorMessage).
 */
export function parseFailedDocumentsCsv(csv: string): FailedDocumentRow[] {
    if (!csv) {
        return [];
    }
    const lines = csv.split(/\r?\n/).filter((l) => l.trim().length > 0);
    if (lines.length <= 1) {
        return [];
    }

    // NOTE: the backend emits the header row with PLAIN commas (SearchDocumentDisplay.GetHeader),
    // but each DATA row is wrapped in quotes and joined by `","` (SearchDocumentDisplay.ToString).
    // So the header must be split on plain commas, data rows on the `","` delimiter.
    const col = resolveColumns(lines[0]);
    const rows: FailedDocumentRow[] = [];

    for (let i = 1; i < lines.length; i++) {
        const cells = splitCsvLine(lines[i]);
        // Require at least enough columns to align the fields we read (through Mode at index 19).
        // Trailing fields (e.g. CompanyName, SupplierErrorCode) are optional and fall back to ''.
        // Rows shorter than this are malformed (e.g. an embedded newline in ErrorMessage) and
        // skipped defensively.
        if (cells.length < col.mode + 1) {
            continue;
        }
        const errorMessage = cells[col.errorMessage] ?? '';
        const externalReceiptId = cells[col.externalReceiptId] ?? '';
        const supplierErrorCode = cells[col.supplierErrorCode] ?? '';
        rows.push({
            documentId: cells[col.documentId] ?? '',
            invoiceNumber: cells[col.invoiceNumber] ?? '',
            countryCode: cells[col.countryCode] ?? '',
            supplier: cells[col.supplier] ?? '',
            scenario: cells[col.scenario] ?? '',
            status: cells[col.status] ?? '',
            errorCode: cells[col.errorCode] ?? '',
            errorMessage,
            externalReceiptId,
            supplierErrorCode,
            endEventTime: cells[col.endEventTime] ?? '',
            startEventTime: cells[col.startEventTime] ?? '',
            mode: cells[col.mode] ?? '',
            companyName: cells[col.companyName] ?? '',
            rootCause: classifyRootCause(errorMessage),
            errorSource: classifyErrorSource(supplierErrorCode, externalReceiptId),
        });
    }
    return rows;
}

// EndEventTime/StartEventTime are emitted as "yyyy-MM-dd hh:mm:ss" with NO timezone suffix.
// `new Date("2026-04-01T10:00:00")` would parse that as the browser's LOCAL time, but binKey groups
// using getUTC* accessors — so a naive parse would shift rows near midnight into a different
// day/week/month bin depending on the viewer's timezone. Parse the components explicitly as UTC so
// binning is deterministic and timezone-independent.
function parseEventDate(value: string): Date | null {
    if (!value) {
        return null;
    }
    const m = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/);
    if (!m) {
        return null;
    }
    const [, y, mo, d, h, mi, s] = m;
    const utc = Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s));
    const date = new Date(utc);
    return isNaN(date.getTime()) ? null : date;
}

function startOfWeek(d: Date): Date {
    const copy = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
    const day = copy.getUTCDay(); // 0 = Sunday
    const diff = (day + 6) % 7; // days since Monday
    copy.setUTCDate(copy.getUTCDate() - diff);
    return copy;
}

/** Bucket key (sortable, yyyy-... prefixed) for a row's StartEventTime given the selected bin. */
export function binKey(value: string, bin: TimeBin): string {
    const d = parseEventDate(value);
    if (!d) {
        return 'Unknown';
    }
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    if (bin === 'Month') {
        return `${y}-${m}`;
    }
    if (bin === 'Week') {
        const ws = startOfWeek(d);
        const wm = String(ws.getUTCMonth() + 1).padStart(2, '0');
        const wd = String(ws.getUTCDate()).padStart(2, '0');
        return `${ws.getUTCFullYear()}-${wm}-${wd}`;
    }
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/**
 * Short, human-friendly axis label for a bin key.
 * Day/Week keys ("yyyy-MM-dd") render as "MM-DD"; Month keys ("yyyy-MM") render as "yyyy-MM".
 * The full key remains available for tooltips. "Unknown" passes through unchanged.
 */
export function formatBinLabel(key: string, bin: TimeBin): string {
    if (key === 'Unknown') {
        return key;
    }
    if (bin === 'Month') {
        return key;
    }
    const parts = key.split('-');
    return parts.length === 3 ? `${parts[1]}-${parts[2]}` : key;
}

function tally(values: string[]): CountItem[] {
    const map = new Map<string, number>();
    for (const v of values) {
        const key = v && v.trim().length > 0 ? v : 'Unknown';
        map.set(key, (map.get(key) ?? 0) + 1);
    }
    return Array.from(map.entries())
        .map(([key, count]) => ({ key, count }))
        .sort((a, b) => b.count - a.count);
}

/** Aggregate parsed rows into everything the view needs (KPIs, breakdowns, heatmap, trend). */
export function buildAnalysis(rows: FailedDocumentRow[], bin: TimeBin): FailureAnalysis {
    const byRootCause = tally(rows.map((r) => r.rootCause)).sort(
        (a, b) =>
            ROOT_CAUSE_ORDER.indexOf(a.key as RootCause) - ROOT_CAUSE_ORDER.indexOf(b.key as RootCause),
    );
    const byCountry = tally(rows.map((r) => r.countryCode));
    const bySupplier = tally(rows.map((r) => r.supplier));
    const byBin = tally(rows.map((r) => binKey(r.startEventTime, bin))).sort((a, b) =>
        a.key.localeCompare(b.key),
    );
    const bySource = tally(rows.map((r) => r.errorSource)).sort(
        (a, b) =>
            ERROR_SOURCE_ORDER.indexOf(a.key as ErrorSource) -
            ERROR_SOURCE_ORDER.indexOf(b.key as ErrorSource),
    );

    const countries = byCountry.map((c) => c.key);
    const presentRootCauses = ROOT_CAUSE_ORDER.filter((rc) =>
        rows.some((r) => r.rootCause === rc),
    );

    // Provider vs Unknown split per root cause, preserving the canonical root-cause ordering.
    const rootCauseBySource: SourceSplit[] = presentRootCauses.map((rc) => {
        const subset = rows.filter((r) => r.rootCause === rc);
        const provider = subset.filter((r) => r.errorSource === 'Provider').length;
        const unknown = subset.length - provider;
        return { key: rc, provider, unknown, total: subset.length };
    });

    const heatmap: HeatmapCell[] = [];
    const cellMap = new Map<string, number>();
    for (const r of rows) {
        const country = r.countryCode && r.countryCode.trim() ? r.countryCode : 'Unknown';
        const cellKey = `${country}||${r.rootCause}`;
        cellMap.set(cellKey, (cellMap.get(cellKey) ?? 0) + 1);
    }
    for (const country of countries) {
        for (const rc of presentRootCauses) {
            const count = cellMap.get(`${country}||${rc}`) ?? 0;
            heatmap.push({ country, rootCause: rc, count });
        }
    }

    return {
        rows,
        total: rows.length,
        byRootCause,
        byCountry,
        bySupplier,
        byBin,
        bySource,
        rootCauseBySource,
        countries,
        rootCauses: presentRootCauses,
        heatmap,
    };
}

function csvCell(value: string): string {
    let v = value ?? '';
    // Formula-injection neutralization (mirrors backend EscapeCsvCell): if the first
    // non-whitespace char is one of = + - @ (or a leading tab/CR that Excel may strip to
    // reveal a formula), prefix with a single quote so spreadsheet clients don't execute it.
    const firstNonWs = v.replace(/^[\s]+/, '')[0];
    if (firstNonWs === '=' || firstNonWs === '+' || firstNonWs === '-' || firstNonWs === '@') {
        v = `'${v}`;
    }
    return `"${v.replace(/"/g, '""')}"`;
}

/** Build a detail CSV (Blob) for a subset of rows — used by heatmap/breakdown click-to-download. */
export function buildDetailCsv(rows: FailedDocumentRow[]): Blob {
    const header = [
        'DocumentId',
        'InvoiceNumber',
        'CompanyName',
        'Country',
        'Supplier',
        'Scenario',
        'Mode',
        'StartEventTime',
        'EndEventTime',
        'ErrorCode',
        'RootCause',
        'ErrorSource',
        'SupplierErrorCode',
        'ErrorMessage',
    ];
    const lines = [header.map(csvCell).join(',')];
    for (const r of rows) {
        lines.push(
            [
                r.documentId,
                r.invoiceNumber,
                r.companyName,
                r.countryCode,
                r.supplier,
                r.scenario,
                r.mode,
                r.startEventTime,
                r.endEventTime,
                r.errorCode,
                r.rootCause,
                r.errorSource,
                r.supplierErrorCode,
                r.errorMessage,
            ]
                .map(csvCell)
                .join(','),
        );
    }
    return new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
}
